import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import { Check, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SYSTEM_BY_KEY } from "@/lib/sensory";
import { useLowStim } from "@/contexts/LowStimContext";
import { TID } from "@/lib/testIds";
import BubbleField from "@/components/BubbleField";
import { speak, stopSpeaking } from "@/lib/tts";

// mode = "student" | "explore"
export default function StudentBox({ mode = "student" }) {
  const { system } = useParams();
  const sys = SYSTEM_BY_KEY[system];
  const nav = useNavigate();
  const { enabled: lowStim } = useLowStim();
  const [bubbles, setBubbles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [guidance, setGuidance] = React.useState(null);

  const backTo = mode === "student" ? "/play/boxes" : "/explore/boxes";
  const listEndpoint = mode === "student" ? `/me/boxes/${system}` : `/public/boxes/${system}`;
  const selectEndpoint = mode === "student" ? "/me/select" : "/public/select";

  React.useEffect(() => {
    if (!sys) return;
    api.get(listEndpoint)
      .then(({ data }) => setBubbles(data.bubbles || []))
      .finally(() => setLoading(false));
  }, [system, sys, listEndpoint]);

  if (!sys) return <div>Unknown box. <Link to={backTo} className="underline">Back</Link></div>;

  const onPop = async (b) => {
    try {
      const { data } = await api.post(selectEndpoint, { intervention_id: b.id, entry: "box" });
      setGuidance(data.intervention);
      speak(`${data.intervention.bubble_name}. ${data.intervention.guidance_text}`);
    } catch (e) {
      setGuidance(b);
    }
  };

  const closeGuidance = () => { stopSpeaking(); setGuidance(null); };

  const Icon = sys.icon;

  return (
    <div data-testid={TID.studentBox} className="relative">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button data-testid={TID.studentBackToBoxes} onClick={() => nav(backTo)} className="sw-pill h-12">
          <ArrowLeft size={16} /> Back to boxes
        </button>
        <div className="display text-xl md:text-2xl font-bold flex items-center gap-2" style={{ color: sys.color }}>
          <Icon size={22} /> {sys.studentLabel}
        </div>
      </div>

      <section
        className="rounded-3xl border-2 p-4 md:p-8 relative overflow-hidden"
        style={{ background: sys.bg, borderColor: sys.border, minHeight: 460 }}
      >
        {loading ? (
          <div className="text-center py-12" style={{ color: sys.color }}>Loading…</div>
        ) : bubbles.length === 0 ? (
          <div className="text-center py-12" style={{ color: sys.color }}>This box is empty right now.</div>
        ) : (
          <BubbleField
            bubbles={bubbles}
            color={sys.color}
            accent={sys.border}
            lowStim={lowStim}
            onPop={onPop}
            testIdFor={(id) => TID.studentBubble(id)}
          />
        )}
      </section>

      <AnimatePresence>
        {guidance && (
          <motion.div
            data-testid={TID.studentGuidance}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={closeGuidance}
          >
            <motion.div
              initial={{ scale: 0.9, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              className="sw-card max-w-lg w-full p-8 text-center" onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff" }}
            >
              <div
                className="mx-auto h-20 w-20 rounded-full flex items-center justify-center text-white"
                style={{ background: `radial-gradient(circle at 32% 28%, #ffffff 0%, ${sys.border} 38%, ${sys.color} 100%)` }}
              >
                {(() => {
                  const I = (guidance.icon && Lucide[guidance.icon]) || Lucide.Sparkles;
                  return <I size={36} className="text-white" />;
                })()}
              </div>
              <h2 className="display text-3xl font-bold mt-4" style={{ color: sys.color }}>{guidance.bubble_name}</h2>
              <p className="mt-3 text-lg text-[var(--sw-ink-soft)] leading-relaxed">{guidance.guidance_text}</p>
              <button
                data-testid={TID.studentGuidanceDone}
                onClick={closeGuidance}
                className="mt-6 sw-pill h-14 px-8 text-lg bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
              >
                <Check size={18} /> Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
